import type { WeeklyAlarmRoutine, WeeklyRoutineWindow } from "@src/services/api/types";
import {
  DaysOfTheWeekSchema,
  EffectiveTimeRangesSchema,
  makeAllDayEffectiveRange,
  TimeRange,
  type DayOfTheWeek,
} from "@visioni/schedules";
import {
  GridState,
  isAllDayRange,
  isTimeRange,
  markAllDaySlots,
  markTimeRangeSlots,
  convertSlotsToTimeRanges,
} from "../grid-utils";
import { type Granularity, getSlotsPerDay } from "../granularity-utils";

const DAYS: DayOfTheWeek[] = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

export function parseWeeklyRoutineToGridState(
  routineData: WeeklyAlarmRoutine,
  granularity: Granularity = 60,
): GridState {
  const gridState: GridState = {};
  const slotsPerDay = getSlotsPerDay(granularity);

  // Parse initial data into grid state
  routineData.windows.forEach((window) => {
    window.days.forEach((day) => {
      // EffectiveTimeRanges is an array-like structure
      const timeRangesArray = Array.isArray(window.timeRanges) ? window.timeRanges : [window.timeRanges];

      timeRangesArray.forEach((range) => {
        if (range && typeof range === "object") {
          if (isAllDayRange(range)) {
            markAllDaySlots(gridState, day, slotsPerDay);
          } else if (isTimeRange(range)) {
            const timeRange = range as {
              start: { hour: number; minute: number };
              end: { hour: number; minute: number };
            };
            markTimeRangeSlots(gridState, day, timeRange, granularity);
          }
        }
      });
    });
  });

  return gridState;
}

export function convertGridStateToWeeklyRoutine(
  gridState: GridState,
  granularity: Granularity = 60,
): WeeklyAlarmRoutine {
  const windows: WeeklyRoutineWindow[] = [];
  const slotsPerDay = getSlotsPerDay(granularity);

  // Group consecutive slots by day
  DAYS.forEach((day) => {
    const slotsForDay: number[] = [];
    for (let slot = 0; slot < slotsPerDay; slot++) {
      if (gridState[`${day}-${slot}`]) {
        slotsForDay.push(slot);
      }
    }

    if (slotsForDay.length > 0) {
      const timeRanges = convertSlotsToTimeRanges(slotsForDay, granularity);

      windows.push({
        days: DaysOfTheWeekSchema.parse([day]),
        timeRanges: EffectiveTimeRangesSchema.parse(
          timeRanges.length === 0 ? [makeAllDayEffectiveRange()] : (timeRanges as [TimeRange, ...TimeRange[]]),
        ),
      });
    }
  });

  return {
    kind: "weekly",
    windows,
  };
}
